// radiusPools — admin surface for IP-pool and package CRUD, feeding
// the secondary-RADIUS Access-Accept construction. Admin-only.
//
//   IP pools:  Mikrotik pool names (v4 or v6) with display metadata
//   Packages:  package name → (ipv4_pool, ipv6_pool) mapping
//
// close/block subscribers are hard-coded (server-side) to the
// "expired-pool" walled garden — that row must exist in radius_ip_pools
// with family='inet'. No IPv6 for expired users.

import type { ApiClient } from "./client.js";

export type RadiusPoolFamily = "inet" | "inet6";

export interface RadiusPool {
  name: string;
  family: RadiusPoolFamily;
  cidr?: string;
  gateway?: string;
  notes?: string;
}

export interface RadiusPackage {
  name: string;
  ipv4_pool: string;
  /** Empty string / undefined = v4-only package. */
  ipv6_pool?: string;
  notes?: string;
}

export interface RadiusPoolsListResponse {
  ok: boolean;
  pools?: RadiusPool[];
  error?: string;
}

export interface RadiusPackagesListResponse {
  ok: boolean;
  packages?: RadiusPackage[];
  error?: string;
}

export interface RadiusPoolsOpResponse {
  ok: boolean;
  error?: string;
}

/** Snapshot shown on the Backup RADIUS page — config coverage, kick
 *  activity, and daemon placeholder. Poll every 15-30s. */
export interface BackupRadiusStatus {
  config: {
    total_subscribers: number;
    with_password: number;
    with_password_pct: number;
    pools_v4: number;
    pools_v6: number;
    expired_pool_exists: boolean;
    total_packages: number;
    active_subscribers: number;
    active_mapped: number;
    active_unmapped: number;
    unmapped_packages?: string[];
  };
  kicks: {
    kicked: number;
    no_session: number;
    already_offline: number;
    router_unknown: number;
    error: number;
  };
  daemon: {
    /** "not_deployed" until Phase 2 lands, then "running"/"degraded". */
    state: "not_deployed" | "running" | "degraded";
    bind_v4?: string;
    bind_v6?: string;
    cache_size: number;
    cache_age_seconds: number;
    auth_last_5m: number;
    reject_rate_last_5m: number;
    p99_latency_ms: number;
  };
}

export interface BackupRadiusStatusResponse {
  ok: boolean;
  status?: BackupRadiusStatus;
  error?: string;
}

/** Editable daemon config. `secret_set` tells the UI whether one
 *  is stored (without revealing its value); the plaintext is only
 *  ever transmitted from browser → server on a save with a NEW
 *  value, never the reverse. */
export interface BackupRadiusConfig {
  enabled: boolean;
  bind_auth: string;
  bind_acct: string;
  secret_set: boolean;
  secret_length?: number;
}

export interface BackupRadiusConfigInput {
  enabled: boolean;
  bind_auth: string;
  bind_acct: string;
  /** Only send when actually rotating. Omit / empty to keep the
   *  existing stored secret. */
  shared_secret?: string;
}

export interface BackupRadiusConfigResponse {
  ok: boolean;
  config?: BackupRadiusConfig;
  error?: string;
}

export interface BackupRadiusConfigSaveResponse {
  ok: boolean;
  restart_required?: boolean;
  error?: string;
}

export function makeRadiusPoolsApi(client: ApiClient) {
  return {
    listPools() {
      return client.get<RadiusPoolsListResponse>("/v1/api/radius/pools");
    },
    savePool(pool: RadiusPool) {
      return client.post<RadiusPoolsOpResponse>("/v1/api/radius/pools", pool);
    },
    deletePool(name: string) {
      return client.delete<RadiusPoolsOpResponse>(`/v1/api/radius/pools?name=${encodeURIComponent(name)}`);
    },
    listPackages() {
      return client.get<RadiusPackagesListResponse>("/v1/api/radius/packages");
    },
    savePackage(pkg: RadiusPackage) {
      return client.post<RadiusPoolsOpResponse>("/v1/api/radius/packages", pkg);
    },
    deletePackage(name: string) {
      return client.delete<RadiusPoolsOpResponse>(`/v1/api/radius/packages?name=${encodeURIComponent(name)}`);
    },
    /** Backup RADIUS control-panel snapshot — config coverage, kick
     *  activity, daemon health placeholder. Poll on ~20s cadence. */
    backupStatus() {
      return client.get<BackupRadiusStatusResponse>("/v1/api/radius/backup_status");
    },
    /** Read the current daemon config (secret redacted). */
    backupConfig() {
      return client.get<BackupRadiusConfigResponse>("/v1/api/radius/backup_config");
    },
    /** Save daemon config. Omit `shared_secret` to leave the stored
     *  value unchanged; pass a new non-empty string to rotate it. */
    saveBackupConfig(cfg: BackupRadiusConfigInput) {
      return client.post<BackupRadiusConfigSaveResponse>("/v1/api/radius/backup_config", cfg);
    },
  };
}

export type RadiusPoolsApi = ReturnType<typeof makeRadiusPoolsApi>;
